import React, { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

// Layout components
import AuthLayout from '@/components/layouts/AuthLayout'
import AppLayout from '@/components/layouts/AppLayout'

// Auth pages
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import ResetPasswordPage from '@/pages/auth/ResetPasswordPage'

// App pages
import DashboardPage from '@/pages/DashboardPage'
import ProjectsPage from '@/pages/projects/ProjectsPage'
import ProjectDetailPage from '@/pages/projects/ProjectDetailPage'
import TasksPage from '@/pages/tasks/TasksPage'
import TaskDetailPage from '@/pages/tasks/TaskDetailPage'
import TimeTrackingPage from '@/pages/time/TimeTrackingPage'
import ClientsPage from '@/pages/clients/ClientsPage'
import ClientDetailPage from '@/pages/clients/ClientDetailPage'
import UsersPage from '@/pages/users/UsersPage'
import ProfilePage from '@/pages/users/ProfilePage'
import SettingsPage from '@/pages/SettingsPage'
import TicketsPage from '@/pages/tickets/TicketsPage'
import ContractsPage from '@/pages/contracts/ContractsPage'
import InvoicesPage from '@/pages/invoices/InvoicesPage'
import ReportsPage from '@/pages/reports/ReportsPage'

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="flex flex-col items-center space-y-4">
      <div className="loading-spinner w-8 h-8 text-primary-600"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
)

// Protected route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />
  }

  return <>{children}</>
}

// Public route wrapper (redirects to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function App() {
  console.log('App component rendering...');
  const { loadUser, isLoading, isAuthenticated } = useAuth()

  // Load user on app start
  useEffect(() => {
    console.log('App: Loading user...');
    loadUser()
  }, [loadUser])

  console.log('App: isLoading =', isLoading, 'isAuthenticated =', isAuthenticated);

  // Show loading screen while checking authentication
  if (isLoading) {
    console.log('App: Showing loading spinner');
    return <LoadingSpinner />
  }

  return (
    <div className="App">
      <Routes>
        {/* Public routes */}
        <Route
          path="/auth/*"
          element={
            <PublicRoute>
              <AuthLayout>
                <Routes>
                  <Route path="login" element={<LoginPage />} />
                  <Route path="register" element={<RegisterPage />} />
                  <Route path="forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="reset-password" element={<ResetPasswordPage />} />
                  <Route path="*" element={<Navigate to="/auth/login" replace />} />
                </Routes>
              </AuthLayout>
            </PublicRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Routes>
                  {/* Dashboard */}
                  <Route path="/dashboard" element={<DashboardPage />} />

                  {/* Projects */}
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/projects/:projectId" element={<ProjectDetailPage />} />

                  {/* Tasks */}
                  <Route path="/tasks" element={<TasksPage />} />
                  <Route path="/tasks/:taskId" element={<TaskDetailPage />} />

                  {/* Time Tracking */}
                  <Route path="/time" element={<TimeTrackingPage />} />

                  {/* Clients */}
                  <Route path="/clients" element={<ClientsPage />} />
                  <Route path="/clients/:clientId" element={<ClientDetailPage />} />

                  {/* Users */}
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/profile" element={<ProfilePage />} />

                  {/* Settings */}
                  <Route path="/settings" element={<SettingsPage />} />

                  {/* Tickets */}
                  <Route path="/tickets" element={<TicketsPage />} />

                  {/* Contracts */}
                  <Route path="/contracts" element={<ContractsPage />} />

                  {/* Invoices */}
                  <Route path="/invoices" element={<InvoicesPage />} />

                  {/* Reports */}
                  <Route path="/reports" element={<ReportsPage />} />

                  {/* Default redirect */}
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Default redirect to login if not authenticated */}
        <Route path="/" element={<Navigate to="/auth/login" replace />} />
      </Routes>
    </div>
  )
}

export default App